/**
 * assets/wall-simulator.js
 * Lógica principal do simulador de quadros na parede.
 * Depende de: wall-simulator-config.js (WALL_SIMULATOR_CONFIG)
 */

(function () {
  'use strict';

  // Confirma que o arquivo carregou e que o config está disponível
  console.log('[SIM] wall-simulator.js carregado. CONFIG disponível:', typeof WALL_SIMULATOR_CONFIG !== 'undefined');

  // ─── Atalhos para os principais elementos DOM ─────────────────────────────

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

  // ─── Estado global ────────────────────────────────────────────────────────

  const state = {
    catalog: [],        // todos os produtos carregados
    pieces: [],         // quadros na cena [ { id, product, variantId, size, paper, frame, x, y, scale } ]
    selectedId: null,   // id da peça selecionada
    nextId: 1,
    activeRoom: null,
  };

  // ─── Inicialização ────────────────────────────────────────────────────────

  function init() {
    if (!$('#lws-simulator')) return; // proteção: só roda na página do simulador

    if (typeof WALL_SIMULATOR_CONFIG === 'undefined') {
      console.error('[SIM] WALL_SIMULATOR_CONFIG não encontrado. Verifique se wall-simulator-config.js está carregado antes de wall-simulator.js.');
      const status = document.getElementById('lws-search-status');
      if (status) { status.textContent = 'Erro de configuração. Contate o suporte.'; status.style.color = 'red'; }
      return;
    }

    const cfg = WALL_SIMULATOR_CONFIG;
    const params = readUrlParams();
    console.log('[SIM] Parâmetros recebidos da URL:', params);

    setupRoomPicker(cfg.rooms);
    setupControls(cfg);
    setupAccordions();
    setupSceneEvents();
    setupPanelToggle();
    setupGlobalActions();

    loadCatalog(cfg).then(() => {
      if (params.product) {
        loadInitialProduct(params.product, params.variant);
      }
    });
  }

  // ─── URL params ──────────────────────────────────────────────────────────

  function readUrlParams() {
    const p = new URLSearchParams(window.location.search);
    return {
      product: p.get('product') || null,
      variant: p.get('variant') ? Number(p.get('variant')) : null,
    };
  }

  // ─── Catálogo ─────────────────────────────────────────────────────────────

  async function loadCatalog(cfg) {
    const status = $('#lws-search-status');
    if (status) status.textContent = cfg.messages.loading;

    // Suporta collectionHandles (array) ou collectionHandle (string legada)
    const handles = cfg.collectionHandles || (cfg.collectionHandle ? [cfg.collectionHandle] : []);
    console.log('[SIM] Coleções a carregar:', handles);

    const allProducts = [];
    const seenHandles = new Set(); // evita duplicatas entre coleções

    for (const handle of handles) {
      console.log(`[SIM] Iniciando carga da coleção: ${handle}`);
      let page = 1;
      let keepGoing = true;

      while (keepGoing) {
        const url = `/collections/${handle}/products.json?limit=${cfg.productFetchLimit}&page=${page}`;
        console.log(`[SIM] Buscando ${handle} p.${page}:`, url);

        try {
          const res = await fetch(url);
          if (!res.ok) throw new Error(`HTTP ${res.status} em ${url}`);
          const data = await res.json();
          const batch = (data.products || []).filter(p => {
            if (seenHandles.has(p.handle)) return false;
            seenHandles.add(p.handle);
            return true;
          });

          console.log(`[SIM] ${handle} p.${page}: ${batch.length} produtos (únicos)`);

          if (batch.length === 0 && (data.products || []).length === 0) {
            keepGoing = false;
          } else {
            allProducts.push(...batch);
            if (status) status.textContent = `${allProducts.length} obras carregadas…`;
            page++;
            if ((data.products || []).length < cfg.productFetchLimit) keepGoing = false;
          }
        } catch (err) {
          console.error(`[SIM] Erro ao carregar coleção "${handle}":`, err);
          if (status) {
            status.textContent = `Erro na coleção "${handle}". Verifique o console.`;
            status.style.color = 'red';
          }
          keepGoing = false; // continua para a próxima coleção
        }
      }
    }

    state.catalog = allProducts;
    console.log(`[SIM] Total de produtos carregados: ${allProducts.length}`);

    if (allProducts.length === 0) {
      if (status) {
        status.textContent = `Nenhum produto encontrado. Verifique collectionHandles no config: ${JSON.stringify(handles)}`;
        status.style.color = 'orange';
      }
      return;
    }

    if (status) {
      status.textContent = `${allProducts.length} obras disponíveis`;
      status.style.color = '';
    }
    logCatalogImages(allProducts);
    renderSearchResults('');
    setupSearch();
  }

  // Diagnóstico: loga todas as imagens disponíveis no catálogo
  function logCatalogImages(products) {
    console.group(`[SIM] 🖼 Imagens do catálogo (${products.length} produtos)`);

    products.forEach(product => {
      const entries = [];

      // Imagem principal
      if (product.featured_image) {
        entries.push({ campo: 'product.featured_image', url: product.featured_image });
      }

      // Array de imagens do produto
      (product.images || []).forEach((img, i) => {
        entries.push({
          campo: `product.images[${i}].src`,
          url: img.src,
          variant_ids: img.variant_ids?.length ? img.variant_ids : 'nenhum',
        });
      });

      // Imagens por variante
      (product.variants || []).forEach(v => {
        if (v.featured_image?.src) {
          entries.push({
            campo: `variant(id:${v.id}).featured_image.src`,
            variante: v.title,
            url: v.featured_image.src,
          });
        }
      });

      if (entries.length > 0) {
        console.groupCollapsed(`📦 ${product.title} (handle: "${product.handle}")`);
        entries.forEach(e => console.log(e));
        console.groupEnd();
      } else {
        console.warn(`⚠️ ${product.title} — nenhuma imagem encontrada`);
      }
    });

    console.groupEnd();
  }

  // ─── Produto inicial (vindo da URL) ──────────────────────────────────────

  async function loadInitialProduct(handle, variantId) {
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const product = await res.json();
      console.log('[SIM] Produto inicial carregado:', product.title);

      // Encontrar variant inicial
      let variant = null;
      if (variantId) {
        variant = product.variants.find(v => v.id === variantId) || null;
      }
      if (!variant) {
        variant = product.variants[0];
      }
      console.log('[SIM] Variante inicial encontrada:', variant?.id, variant?.title);

      // Decompor opções da variante
      const { size, paper, frame } = extractOptionsFromVariant(variant);

      addPiece(product, { variantId: variant.id, size, paper, frame });
    } catch (err) {
      console.error('[SIM] Erro ao carregar produto inicial:', err);
    }
  }

  // ─── Extrair opções da variante ──────────────────────────────────────────

  /**
   * Tenta mapear option1/option2/option3 para size, paper e frame
   * usando os arrays de configuração.
   */
  function extractOptionsFromVariant(variant) {
    if (!variant) return defaultOptions();

    const cfg = WALL_SIMULATOR_CONFIG;
    const opts = [variant.option1, variant.option2, variant.option3].filter(Boolean);

    const size  = opts.find(o => cfg.sizes.some(s => normalize(s) === normalize(o)))
                  || cfg.sizes[0];
    const paper = opts.find(o => cfg.papers.some(p => normalize(p.id) === normalize(o) || normalize(p.label) === normalize(o)))
                  || cfg.papers[0].id;
    const frame = opts.find(o => cfg.frames.some(f => normalize(f.id) === normalize(o) || normalize(f.label) === normalize(o)))
                  || cfg.frames[0].id;

    return { size, paper: normalizePaperId(paper), frame: normalizeFrameId(frame) };
  }

  function defaultOptions() {
    const cfg = WALL_SIMULATOR_CONFIG;
    return { size: cfg.sizes[0], paper: cfg.papers[0].id, frame: cfg.frames[0].id };
  }

  /** Retorna o id canônico do papel */
  function normalizePaperId(value) {
    const cfg = WALL_SIMULATOR_CONFIG;
    const found = cfg.papers.find(p =>
      normalize(p.id) === normalize(value) || normalize(p.label) === normalize(value)
    );
    return found ? found.id : cfg.papers[0].id;
  }

  /** Retorna o id canônico da moldura */
  function normalizeFrameId(value) {
    const cfg = WALL_SIMULATOR_CONFIG;
    const found = cfg.frames.find(f =>
      normalize(f.id) === normalize(value) || normalize(f.label) === normalize(value)
    );
    return found ? found.id : cfg.frames[0].id;
  }

  function normalize(str) {
    return String(str).toLowerCase().trim();
  }

  // ─── Busca ────────────────────────────────────────────────────────────────

  function setupSearch() {
    const input = $('#lws-search-input');
    if (!input) return;
    input.addEventListener('input', debounce(() => {
      renderSearchResults(input.value.trim());
    }, 250));
  }

  function renderSearchResults(query) {
    const container = $('#lws-search-results');
    if (!container) return;

    // Desconecta observer anterior se existir
    if (state._searchObserver) {
      state._searchObserver.disconnect();
      state._searchObserver = null;
    }

    const cfg = WALL_SIMULATOR_CONFIG;
    const q = normalize(query);
    const filtered = q
      ? state.catalog.filter(p => normalize(p.title).includes(q) || normalize(p.vendor || '').includes(q))
      : state.catalog;

    const PAGE_SIZE = 8;
    const visible = filtered.slice(0, cfg.visibleSearchLimit);

    const status = $('#lws-search-status');
    if (status) {
      if (filtered.length === 0) {
        status.textContent = cfg.messages.noResults;
      } else if (filtered.length > cfg.visibleSearchLimit) {
        status.textContent = `${filtered.length} obras — refine a busca`;
      } else {
        status.textContent = `${filtered.length} obra${filtered.length !== 1 ? 's' : ''}`;
      }
    }

    if (visible.length === 0) {
      container.innerHTML = `<p class="lws-search__empty">${cfg.messages.noResults}</p>`;
      return;
    }

    // Limpa container e monta grade com scroll infinito
    container.innerHTML = '';

    const grid = document.createElement('div');
    grid.className = 'lws-search__grid';
    container.appendChild(grid);

    // Sentinel: elemento invisível no fim da grade — dispara IntersectionObserver
    const sentinel = document.createElement('div');
    sentinel.className = 'lws-search__sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    grid.appendChild(sentinel);

    let loaded = 0;
    let loading = false;

    function buildCard(product) {
      const img = getProductImage(product);
      const btn = document.createElement('button');
      btn.className = 'lws-card';
      btn.dataset.productHandle = product.handle;
      btn.setAttribute('aria-label', `Adicionar ${product.title} à composição`);
      btn.innerHTML = `
        <div class="lws-card__img-wrap">
          ${img
            ? `<img class="lws-card__img" src="${img}" alt="${escapeHtml(product.title)}" loading="lazy" />`
            : `<div class="lws-card__img-placeholder" aria-hidden="true"></div>`
          }
        </div>
        <span class="lws-card__title">${escapeHtml(product.title)}</span>
      `;
      btn.addEventListener('click', () => {
        const p = state.catalog.find(x => x.handle === product.handle);
        if (p) addPiece(p);
      });
      return btn;
    }

    function loadNextPage() {
      if (loading || loaded >= visible.length) return;
      loading = true;

      const batch = visible.slice(loaded, loaded + PAGE_SIZE);
      batch.forEach(p => grid.insertBefore(buildCard(p), sentinel));
      loaded += batch.length;
      loading = false;

      // Remove sentinel se não há mais itens
      if (loaded >= visible.length) {
        observer.unobserve(sentinel);
        sentinel.remove();
      }
    }

    // IntersectionObserver: dispara loadNextPage quando sentinel entra na view
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadNextPage(); },
      { root: grid, threshold: 0.1 }
    );
    observer.observe(sentinel);

    // Carrega a primeira página imediatamente
    loadNextPage();

    // Guarda observer para desconectar ao refazer a busca
    state._searchObserver = observer;
  }

  // ─── Composição (peças na cena) ──────────────────────────────────────────

  function addPiece(product, options = {}) {
    const cfg = WALL_SIMULATOR_CONFIG;
    const scene = $('#lws-scene');
    if (!scene) return;

    const size  = options.size  || cfg.sizes[0];
    const paper = options.paper || cfg.papers[0].id;
    const frame = options.frame || cfg.frames[0].id;
    const variantId = options.variantId || findVariantId(product, size, paper, frame);

    // Posição inicial: centro da cena com leve offset por índice
    const sceneRect = scene.getBoundingClientRect();
    const x = (sceneRect.width  / 2) - 80 + (state.pieces.length * 20);
    const y = (sceneRect.height / 2) - 80 + (state.pieces.length * 20);

    const piece = {
      id: state.nextId++,
      product,
      variantId,
      size,
      paper,
      frame,
      x,
      y,
      scale: 1,
    };

    state.pieces.push(piece);
    console.log('[SIM] Produto adicionado à composição:', product.title, '| size:', size, '| paper:', paper, '| frame:', frame);

    renderPiece(piece);
    selectPiece(piece.id);
    updateSceneEmpty();
  }

  function renderPiece(piece) {
    const scene = $('#lws-scene');
    if (!scene) return;
    const cfg = WALL_SIMULATOR_CONFIG;

    const baseW   = cfg.baseWidthBySize[piece.size] || 180;
    const baseH   = Math.round(baseW * cfg.defaultAspectRatio);
    const fw      = cfg.frameThickness[piece.frame] || 0;
    const frameColor = (cfg.frames.find(f => f.id === piece.frame) || {}).color || 'transparent';
    const imgSrc  = getVariantImage(piece.product, piece.variantId);

    const el = document.createElement('div');
    el.className = 'lws-piece';
    el.dataset.pieceId = piece.id;
    el.style.setProperty('--lws-piece-x', `${piece.x}px`);
    el.style.setProperty('--lws-piece-y', `${piece.y}px`);
    el.style.setProperty('--lws-piece-scale', piece.scale);
    el.style.setProperty('--lws-frame-color', frameColor);
    el.style.setProperty('--lws-frame-w', `${fw}px`);
    el.style.setProperty('--lws-base-w', `${baseW}px`);
    el.style.setProperty('--lws-base-h', `${baseH}px`);

    el.innerHTML = `
      <div class="lws-piece__frame">
        ${imgSrc
          ? `<img class="lws-piece__img" src="${imgSrc}" alt="${escapeHtml(piece.product.title)}" draggable="false" />`
          : `<div class="lws-piece__img-placeholder" aria-hidden="true"></div>`
        }
      </div>
      <button class="lws-piece__select-hint" aria-label="Selecionar ${escapeHtml(piece.product.title)}" tabindex="0"></button>
    `;

    el.addEventListener('mousedown', (e) => onPieceMousedown(e, piece.id));
    el.addEventListener('touchstart', (e) => onPieceTouchstart(e, piece.id), { passive: false });
    el.addEventListener('click', () => selectPiece(piece.id));

    scene.appendChild(el);
  }

  function updatePieceElement(piece) {
    const cfg = WALL_SIMULATOR_CONFIG;
    const el = $(`[data-piece-id="${piece.id}"]`);
    if (!el) return;

    const baseW   = cfg.baseWidthBySize[piece.size] || 180;
    const baseH   = Math.round(baseW * cfg.defaultAspectRatio);
    const fw      = cfg.frameThickness[piece.frame] || 0;
    const frameColor = (cfg.frames.find(f => f.id === piece.frame) || {}).color || 'transparent';
    const imgSrc  = getVariantImage(piece.product, piece.variantId);

    el.style.setProperty('--lws-piece-x', `${piece.x}px`);
    el.style.setProperty('--lws-piece-y', `${piece.y}px`);
    el.style.setProperty('--lws-piece-scale', piece.scale);
    el.style.setProperty('--lws-frame-color', frameColor);
    el.style.setProperty('--lws-frame-w', `${fw}px`);
    el.style.setProperty('--lws-base-w', `${baseW}px`);
    el.style.setProperty('--lws-base-h', `${baseH}px`);

    const img = el.querySelector('.lws-piece__img');
    if (img && imgSrc) img.src = imgSrc;
  }

  function removePiece(id) {
    state.pieces = state.pieces.filter(p => p.id !== id);
    const el = $(`[data-piece-id="${id}"]`);
    if (el) el.remove();
    if (state.selectedId === id) {
      state.selectedId = null;
      updateControlsPanel(null);
    }
    updateSceneEmpty();
    console.log('[SIM] Peça removida:', id);
  }

  function duplicatePiece(id) {
    const source = state.pieces.find(p => p.id === id);
    if (!source) return;
    addPiece(source.product, {
      variantId: source.variantId,
      size:      source.size,
      paper:     source.paper,
      frame:     source.frame,
    });
  }

  function centerPiece(id) {
    const piece = state.pieces.find(p => p.id === id);
    const scene = $('#lws-scene');
    if (!piece || !scene) return;
    const rect = scene.getBoundingClientRect();
    piece.x = (rect.width  / 2) - 80;
    piece.y = (rect.height / 2) - 80;
    updatePieceElement(piece);
  }

  function resetComposition() {
    state.pieces.forEach(p => $(`[data-piece-id="${p.id}"]`)?.remove());
    state.pieces = [];
    state.selectedId = null;
    updateControlsPanel(null);
    updateSceneEmpty();
    console.log('[SIM] Composição resetada.');
  }

  // ─── Seleção ──────────────────────────────────────────────────────────────

  function selectPiece(id) {
    // Remove seleção anterior
    $$('.lws-piece--selected').forEach(el => el.classList.remove('lws-piece--selected'));

    state.selectedId = id;
    const piece = state.pieces.find(p => p.id === id);
    if (!piece) return;

    $(`[data-piece-id="${id}"]`)?.classList.add('lws-piece--selected');
    updateControlsPanel(piece);
    console.log('[SIM] Peça selecionada:', id, piece.product.title);
  }

  // ─── Painel de controles ─────────────────────────────────────────────────

  function setupControls(cfg) {
    // Popula selects de tamanho e papel
    const sizeSelect = $('#lws-ctrl-size');
    if (sizeSelect) {
      sizeSelect.innerHTML = cfg.sizes.map(s =>
        `<option value="${s}">${s}</option>`
      ).join('');
    }

    const paperSelect = $('#lws-ctrl-paper');
    if (paperSelect) {
      paperSelect.innerHTML = cfg.papers.map(p =>
        `<option value="${p.id}">${p.label}</option>`
      ).join('');
    }

    // Frame picker
    const framePicker = $('#lws-ctrl-frame');
    if (framePicker) {
      framePicker.innerHTML = cfg.frames.map(f => `
        <button
          class="lws-frame-swatch"
          data-frame-id="${f.id}"
          title="${f.label}"
          aria-label="${f.label}"
          style="--swatch-color: ${f.color}"
        ></button>
      `).join('');
    }

    // Listeners
    $('#lws-ctrl-size')?.addEventListener('change', e => onControlChange('size', e.target.value));
    $('#lws-ctrl-paper')?.addEventListener('change', e => onControlChange('paper', e.target.value));
    $('#lws-ctrl-scale')?.addEventListener('input', e => {
      onControlChange('scale', parseFloat(e.target.value));
      const val = $('#lws-ctrl-scale-val');
      if (val) val.textContent = `${parseFloat(e.target.value).toFixed(2)}×`;
    });

    // Frame swatches
    $$('[data-frame-id]').forEach(btn => {
      btn.addEventListener('click', () => onControlChange('frame', btn.dataset.frameId));
    });

    // Ações de peça (centralizar / duplicar / remover)
    $$('[data-piece-action]').forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.pieceAction;
        const id = state.selectedId;
        if (!id) return;
        if (action === 'center')    centerPiece(id);
        if (action === 'duplicate') duplicatePiece(id);
        if (action === 'remove')    removePiece(id);
      });
    });
  }

  function onControlChange(prop, value) {
    const piece = state.pieces.find(p => p.id === state.selectedId);
    if (!piece) return;

    piece[prop] = value;

    if (['size', 'paper', 'frame'].includes(prop)) {
      const newVariantId = findVariantId(piece.product, piece.size, piece.paper, piece.frame);
      const warning = $('#lws-variant-warning');
      if (newVariantId) {
        piece.variantId = newVariantId;
        if (warning) warning.hidden = true;
        console.log(`[SIM] Variante encontrada para peça ${piece.id}:`, newVariantId);
      } else {
        if (warning) warning.hidden = false;
        console.warn(`[SIM] Variante não encontrada: size=${piece.size} paper=${piece.paper} frame=${piece.frame}`);
      }
    }

    updatePieceElement(piece);

    // Sincroniza seleção visual dos swatches
    if (prop === 'frame') {
      $$('[data-frame-id]').forEach(btn => {
        btn.classList.toggle('lws-frame-swatch--active', btn.dataset.frameId === value);
      });
    }
  }

  function updateControlsPanel(piece) {
    const panel = $('#lws-controls');
    if (!panel) return;

    if (!piece) {
      // Fecha o accordion de controles quando nada está selecionado
      setAccordionOpen('controls', false);
      return;
    }

    // Abre o accordion de controles automaticamente ao selecionar peça
    setAccordionOpen('controls', true);

    // Atualiza label do trigger com nome da obra (truncado)
    const label = $('#lws-selected-label');
    if (label) {
      const name = piece.product.title.length > 22
        ? piece.product.title.slice(0, 22) + '…'
        : piece.product.title;
      label.textContent = name;
    }

    const sizeSelect = $('#lws-ctrl-size');
    if (sizeSelect) sizeSelect.value = piece.size;

    const paperSelect = $('#lws-ctrl-paper');
    if (paperSelect) paperSelect.value = piece.paper;

    const scaleRange = $('#lws-ctrl-scale');
    if (scaleRange) scaleRange.value = piece.scale;
    const scaleVal = $('#lws-ctrl-scale-val');
    if (scaleVal) scaleVal.textContent = `${parseFloat(piece.scale).toFixed(2)}×`;

    $$('[data-frame-id]').forEach(btn => {
      btn.classList.toggle('lws-frame-swatch--active', btn.dataset.frameId === piece.frame);
    });

    const warning = $('#lws-variant-warning');
    if (warning) warning.hidden = !!piece.variantId;
  }

  // ─── Ambientes ────────────────────────────────────────────────────────────

  function setupRoomPicker(rooms) {
    const picker = $('#lws-room-picker');
    if (!picker) return;

    picker.innerHTML = rooms.map((r, i) => `
      <button
        class="lws-room-swatch${i === 0 ? ' lws-room-swatch--active' : ''}"
        data-room-id="${r.id}"
        data-room-color="${r.color}"
        title="${r.label}"
        aria-label="${r.label}"
        style="--room-color: ${r.color}"
      ></button>
    `).join('');

    // Aplica o ambiente padrão
    applyRoom(rooms[0].color);
    state.activeRoom = rooms[0].id;

    picker.querySelectorAll('[data-room-id]').forEach(btn => {
      btn.addEventListener('click', () => {
        $$('.lws-room-swatch--active').forEach(b => b.classList.remove('lws-room-swatch--active'));
        btn.classList.add('lws-room-swatch--active');
        state.activeRoom = btn.dataset.roomId;
        applyRoom(btn.dataset.roomColor);
      });
    });
  }

  function applyRoom(color) {
    const scene = $('#lws-scene');
    if (scene) scene.style.setProperty('--lws-room-color', color);
  }

  // ─── Drag & Drop ──────────────────────────────────────────────────────────

  let drag = null;

  function onPieceMousedown(e, id) {
    if (e.button !== 0) return;
    e.preventDefault();
    selectPiece(id);
    const piece = state.pieces.find(p => p.id === id);
    if (!piece) return;

    const scene = $('#lws-scene');
    const sceneRect = scene.getBoundingClientRect();

    drag = {
      id,
      startX: e.clientX - sceneRect.left - piece.x,
      startY: e.clientY - sceneRect.top  - piece.y,
    };
  }

  function onPieceTouchstart(e, id) {
    e.preventDefault();
    selectPiece(id);
    const piece = state.pieces.find(p => p.id === id);
    if (!piece) return;

    const scene = $('#lws-scene');
    const sceneRect = scene.getBoundingClientRect();
    const touch = e.touches[0];

    drag = {
      id,
      startX: touch.clientX - sceneRect.left - piece.x,
      startY: touch.clientY - sceneRect.top  - piece.y,
    };
  }

  function setupSceneEvents() {
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup',   onMouseUp);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend',  onTouchEnd);

    // Clique na cena sem target de peça → deseleciona
    $('#lws-scene')?.addEventListener('click', e => {
      if (!e.target.closest('.lws-piece')) {
        state.selectedId = null;
        $$('.lws-piece--selected').forEach(el => el.classList.remove('lws-piece--selected'));
        updateControlsPanel(null);
      }
    });
  }

  function onMouseMove(e) {
    if (!drag) return;
    movePiece(drag, e.clientX, e.clientY);
  }

  function onMouseUp() {
    drag = null;
  }

  function onTouchMove(e) {
    if (!drag) return;
    e.preventDefault();
    const touch = e.touches[0];
    movePiece(drag, touch.clientX, touch.clientY);
  }

  function onTouchEnd() {
    drag = null;
  }

  function movePiece(dragState, clientX, clientY) {
    const piece = state.pieces.find(p => p.id === dragState.id);
    const scene = $('#lws-scene');
    if (!piece || !scene) return;

    const sceneRect = scene.getBoundingClientRect();
    piece.x = clientX - sceneRect.left - dragState.startX;
    piece.y = clientY - sceneRect.top  - dragState.startY;

    const el = $(`[data-piece-id="${piece.id}"]`);
    if (el) {
      el.style.setProperty('--lws-piece-x', `${piece.x}px`);
      el.style.setProperty('--lws-piece-y', `${piece.y}px`);
    }
  }

  // ─── Accordion ───────────────────────────────────────────────────────────

  function setupAccordions() {
    $$('.lws-accordion__trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        const bodyId = trigger.getAttribute('aria-controls');
        const body = document.getElementById(bodyId);
        if (!body) return;
        trigger.setAttribute('aria-expanded', String(!isOpen));
        body.hidden = isOpen;
      });
    });
  }

  function setAccordionOpen(accordionKey, open) {
    const acc = $(`[data-accordion="${accordionKey}"]`);
    if (!acc) return;
    const trigger = acc.querySelector('.lws-accordion__trigger');
    const bodyId  = trigger?.getAttribute('aria-controls');
    const body    = bodyId ? document.getElementById(bodyId) : null;
    if (!trigger || !body) return;
    trigger.setAttribute('aria-expanded', String(open));
    body.hidden = !open;
  }

  // ─── Painel toggle ────────────────────────────────────────────────────────

  function setupPanelToggle() {
    const btn = $('#lws-panel-toggle');
    const panel = $('#lws-panel');
    if (!btn || !panel) return;

    btn.addEventListener('click', () => {
      const collapsed = panel.classList.toggle('lws-panel--collapsed');
      btn.setAttribute('aria-expanded', String(!collapsed));
      btn.querySelector('svg')?.style.setProperty('transform', collapsed ? 'rotate(180deg)' : '');
    });
  }

  // ─── Ações globais ────────────────────────────────────────────────────────

  function setupGlobalActions() {
    $('#lws-btn-reset')?.addEventListener('click', () => {
      if (state.pieces.length === 0) return;
      if (confirm('Resetar a composição? Todos os quadros serão removidos.')) {
        resetComposition();
      }
    });

    $('#lws-btn-buy')?.addEventListener('click', buyComposition);
  }

  // ─── Variante ─────────────────────────────────────────────────────────────

  /**
   * Encontra o ID da variante combinando size + paper + frame
   * verificando option1, option2, option3 e o campo title.
   */
  function findVariantId(product, size, paper, frame) {
    if (!product?.variants) return null;
    const cfg = WALL_SIMULATOR_CONFIG;
    const paperLabel = (cfg.papers.find(p => p.id === paper) || {}).label || paper;
    const frameLabel = (cfg.frames.find(f => f.id === frame) || {}).label || frame;

    for (const v of product.variants) {
      const opts = [v.option1, v.option2, v.option3, v.title].map(o => normalize(o || ''));
      const hasSize  = opts.some(o => o === normalize(size));
      const hasPaper = opts.some(o => o === normalize(paper) || o === normalize(paperLabel));
      const hasFrame = opts.some(o => o === normalize(frame) || o === normalize(frameLabel));

      if (hasSize && hasPaper && hasFrame) {
        console.log(`[SIM] Variante encontrada: ${v.id} (${v.title})`);
        return v.id;
      }
    }

    // Fallback: só size + paper (sem moldura)
    for (const v of product.variants) {
      const opts = [v.option1, v.option2, v.option3, v.title].map(o => normalize(o || ''));
      const hasSize  = opts.some(o => o === normalize(size));
      const hasPaper = opts.some(o => o === normalize(paper) || o === normalize(paperLabel));
      if (hasSize && hasPaper) {
        console.log(`[SIM] Variante encontrada (fallback sem moldura): ${v.id}`);
        return v.id;
      }
    }

    // Fallback final: primeira variante
    console.warn('[SIM] Nenhuma variante exata encontrada. Usando a primeira.');
    return product.variants[0]?.id || null;
  }

  // ─── Carrinho ─────────────────────────────────────────────────────────────

  async function buyComposition() {
    const cfg = WALL_SIMULATOR_CONFIG;

    if (state.pieces.length === 0) {
      showToast(cfg.messages.emptyComposition, 'warn');
      return;
    }

    const items = state.pieces.map(piece => {
      const paperLabel = (cfg.papers.find(p => p.id === piece.paper) || {}).label || piece.paper;
      const frameLabel = (cfg.frames.find(f => f.id === piece.frame) || {}).label || piece.frame;
      return {
        id: piece.variantId,
        quantity: 1,
        properties: {
          'Simulado na parede': 'Sim',
          'Tamanho': piece.size,
          'Papel': paperLabel,
          'Moldura': frameLabel,
        },
      };
    });

    console.log('[SIM] Itens enviados ao carrinho:', items);

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      console.log('[SIM] Carrinho: sucesso', data);
      showToast(cfg.messages.cartSuccess, 'success');

      // Redireciona para o carrinho após breve delay
      setTimeout(() => { window.location.href = '/cart'; }, 1200);
    } catch (err) {
      console.error('[SIM] Carrinho: erro', err);
      showToast(cfg.messages.cartError, 'error');
    }
  }

  // ─── Utilitários de imagem ────────────────────────────────────────────────

  function getVariantImage(product, variantId) {
    if (!product) return null;

    // Procura imagem específica da variante
    if (variantId && product.images) {
      for (const img of product.images) {
        if (img.variant_ids && img.variant_ids.includes(variantId)) {
          return img.src;
        }
      }
    }

    // Fallback: imagem principal
    return getProductImage(product);
  }

  function getProductImage(product) {
    if (!product) return null;
    return product.featured_image || product.images?.[0]?.src || null;
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  function updateSceneEmpty() {
    const empty = $('#lws-scene-empty');
    if (!empty) return;
    empty.hidden = state.pieces.length > 0;
  }

  function showToast(message, type = 'info') {
    const toast = $('#lws-toast');
    if (!toast) return;
    toast.textContent = message;
    toast.className = `lws-toast lws-toast--${type}`;
    toast.hidden = false;
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => { toast.hidden = true; }, 3500);
  }

  function debounce(fn, ms) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();